/* eslint-disable no-restricted-globals */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './Slider.sass';

class Slider extends Component {
  constructor(props) {
    super(props);
    const { min, max } = props.boundary;

    this.state = {
      value: { min, max },
      dragging: false,
      minLabelHovering: false,
      maxLabelHovering: false,
      currentControl: null
    };

    this.mapParamsToMinDraggableStyle = this.mapParamsToMinDraggableStyle.bind(this);
    this.mapParamsToMaxDraggableStyle = this.mapParamsToMaxDraggableStyle.bind(this);
    this.mapParamsToRangeStyle = this.mapParamsToRangeStyle.bind(this);
    this.handleSliderMouseDown = this.handleSliderMouseDown.bind(this);
    this.handleSliderMouseMove = this.handleSliderMouseMove.bind(this);
    this.handleSliderMouseUp = this.handleSliderMouseUp.bind(this);
    this.handleIncrement = this.handleIncrement.bind(this);
    this.handleDecrement = this.handleDecrement.bind(this);
    this.broadcastChange = this.broadcastChange.bind(this);
    this.forceSetDraggerPosition = this.forceSetDraggerPosition.bind(this);
  }

  componentWillUpdate(_, nextState) {
    if (this.state.dragging && !nextState.dragging) {
      this.broadcastChange();
    }
  }

  getCurrentState() {
    const { mode, boundary: { min: minBound } } = this.props;
    const { value } = this.state;

    switch (mode) {
      default:
      case 'range':
        return { mode, ...value };
      case 'exact':
        return { mode, value: value.max };
      case 'maximum':
        return { mode, min: minBound, max: value.max };
    }
  }

  broadcastChange() {
    const { onChange } = this.props;
    onChange(this.getCurrentState());
  }

  mapParamsToMinDraggableStyle() {
    const { mode, boundary: { min, max } } = this.props;
    let visibility = false;
    let left = 0;

    if (mode === 'range') {
      visibility = true;
      const { value: { min: minValue } } = this.state;
      left = (minValue / (max - min)) * 100;
    }

    return {
      display: (visibility ? 'inline-block' : 'none'),
      left: `calc(${left}% - 5pt)`
    };
  }

  mapParamsToMaxDraggableStyle() {
    const { boundary: { min, max } } = this.props;
    const { value: { max: maxValue } } = this.state;
    const left = ((maxValue - min) / (max - min)) * 100;

    return { left: `calc(${left}% - 5pt)` };
  }

  mapParamsToRangeStyle() {
    const { mode, boundary: { min, max } } = this.props;
    const { value: { min: minValue, max: maxValue } } = this.state;
    let visibility = true;
    let left = 0;
    let width = 100;

    switch (mode) {
      case 'exact':
        visibility = false;
        break;
      case 'range':
        width = ((maxValue - minValue) / (max - min)) * 100;
        left = (minValue / (max - min)) * 100;
        break;
      case 'maximum':
      default:
        width = ((maxValue - min) / (max - min)) * 100;
    }

    return {
      display: (visibility ? 'inline-block' : 'none'),
      left: `${left}%`,
      width: `${width}%`
    };
  }

  handleSliderMouseDown(e) {
    const { boundary: { min, max }, step } = this.props;
    const { value: { min: currentMin, max: currentMax } } = this.state;
    const newState = { dragging: true };
    const { barElement } = this;
    const { x: offsetX, width } = barElement.getBoundingClientRect();

    const ratio = (e.clientX - offsetX) / width;
    let newValue = (max - min) * ratio;
    newValue -= (newValue - min) % step;

    /* Nearest draggable will be fixed to be controlled */
    if (Math.abs(newValue - currentMax) < Math.abs(newValue - currentMin)) {
      newState.currentControl = 'max';
    } else {
      newState.currentControl = 'min';
    }

    /* If both value are the same, then determined by the position of the mouse */
    if (currentMax === currentMin) {
      newState.currentControl = newValue > currentMax ? 'max' : 'min';
    }

    this.setState(newState);
  }

  handleSliderMouseMove(e) {
    const {
      dragging,
      currentControl,
      value: { max: currentMax, min: currentMin }
    } = this.state;
    const {
      mode,
      onDragging,
      boundary: { min, max },
      step
    } = this.props;
    const { barElement } = this;

    const { x: offsetX, width } = barElement.getBoundingClientRect();

    if (dragging) {
      const newState = {};

      const ratio = (e.clientX - offsetX) / width;

      /* Adjust new value... */
      let newValue = (max - min) * ratio;

      if (newValue === min || newValue === max) {
        return;
      } else if (newValue < min) {
        newValue = min;
      } else if (newValue > max) {
        newValue = max;
      } else {
        const remainder = (newValue - min) % step;
        newValue -= remainder;

        /* If remainder is larger than half of step => Go to next step */
        if (remainder > step / 2) { newValue += step; }
      }

      switch (mode) {
        default:
        case 'maximum':
          onDragging({ mode, min: 0, max: newValue });
          newState.value = { max: newValue, min: 0 };
          break;
        case 'exact':
          onDragging({ mode, value: newValue });
          newState.value = { max: newValue, min: 0 };
          break;
        case 'range':
          if (currentControl === 'max') {
            if (newValue < currentMin) { break; }
            newState.value = { max: newValue, min: currentMin };
          } else if (currentControl === 'min') {
            if (newValue > currentMax) { break; }
            newState.value = { min: newValue, max: currentMax };
          }
          onDragging({ mode, ...newState.value });
      }

      this.setState(newState);
    }
  }

  handleSliderMouseUp() {
    this.setState({ dragging: false });
  }

  handleIncrement() {
    const { boundary: { max: maxBound }, step } = this.props;
    const { value: { min, max } } = this.state;
    const newState = { value: { min, max } };
    if (max + step >= maxBound) {
      newState.value.max = maxBound;
    } else {
      newState.value.max = max + step;
    }

    this.setState(newState, function callback() {
      this.broadcastChange();
    });
  }

  handleDecrement() {
    const { boundary: { min: minBound }, step, mode } = this.props;
    const { value: { min, max } } = this.state;
    const newState = { value: { min, max } };

    switch (mode) {
      default:
      case 'range':
        if (min - step <= minBound) {
          newState.value.min = minBound;
        } else {
          newState.value.min = min - step;
        }
        break;
      case 'exact':
      case 'maximum':
        if (max - step <= minBound) {
          newState.value.max = minBound;
        } else {
          newState.value.max = max - step;
        }
    }

    this.setState(newState, function callback() {
      this.broadcastChange();
    });
  }

  forceSetDraggerPosition({ min, max }) {
    const { mode } = this.props;
    if (mode === 'range') {
      this.setState({ value: { min, max } });
    } else if (mode === 'maximum') {
      this.setState({ value: { min: 0, max } });
    } else if (mode === 'exact') {
      this.setState({ value: { min: max, max } });
    }
  }

  render() {
    const {
      style,
      barStyle,
      draggableStyle,
      rangeStyle,
      mode,
      disabled
    } = this.props;
    const {
      dragging,
      minLabelHovering,
      maxLabelHovering,
      value: { min, max }
    } = this.state;

    const showFloatingLabel = dragging || minLabelHovering || maxLabelHovering;
    const minLabelDisplay = mode === 'range' ? min : '-';

    return (
      <div
        className={`Slider ${disabled ? 'disabled' : ''}`}
        style={style}
        onMouseDown={this.handleSliderMouseDown}
        onMouseMove={this.handleSliderMouseMove}
        onMouseUp={this.handleSliderMouseUp}
      >
        <div
          className="slider-bar-label"
          onMouseEnter={() => { this.setState({ minLabelHovering: true }); }}
          onMouseLeave={() => { this.setState({ minLabelHovering: false }); }}
        >
          {
            minLabelHovering ?
            (
              <button onClick={this.handleDecrement}>
                <span className="fa fa-minus" />
              </button>
            ) : minLabelDisplay
          }
        </div>

        <div ref={(c) => { this.barElement = c; }} className="slider-bar" style={barStyle}>
          <span
            ref={(c) => { this.minDraggable = c; }}
            className={`slider-draggable ${showFloatingLabel ? 'show-floating-label' : ''}`}
            value={min}
            style={{ ...draggableStyle, ...this.mapParamsToMinDraggableStyle() }}
          />

          <span
            ref={(c) => { this.maxDraggable = c; }}
            className={`slider-draggable ${showFloatingLabel ? 'show-floating-label' : ''}`}
            value={max}
            style={{ ...draggableStyle, ...this.mapParamsToMaxDraggableStyle() }}
          />

          <span
            className="slider-range"
            style={{ ...rangeStyle, ...this.mapParamsToRangeStyle() }}
          />
        </div>

        <div
          className="slider-bar-label"
          onMouseEnter={() => { this.setState({ maxLabelHovering: true }); }}
          onMouseLeave={() => { this.setState({ maxLabelHovering: false }); }}
        >

          {
            maxLabelHovering ?
              (
                <button onClick={this.handleIncrement}>
                  <span className="fa fa-plus" />
                </button>
              ) : max
          }
        </div>
      </div>
    );
  }
}

Slider.propTypes = {
  mode: PropTypes.oneOf(['maximum', 'range', 'exact']).isRequired,
  boundary: PropTypes.shape({
    max: PropTypes.number.isRequired,
    min: PropTypes.number.isRequired
  }).isRequired,
  disabled: PropTypes.bool,
  step: PropTypes.number,
  style: PropTypes.object,
  barStyle: PropTypes.object,
  draggableStyle: PropTypes.object,
  rangeStyle: PropTypes.object,
  onDragging: PropTypes.func,
  onChange: PropTypes.func
};

Slider.defaultProps = {
  step: 1,
  disabled: false,
  style: {},
  barStyle: {},
  draggableStyle: {},
  rangeStyle: {},
  onDragging() { },
  onChange() { }
};

export default Slider;



// WEBPACK FOOTER //
// ./src/components/Slider.jsx